import { createSlice } from "@reduxjs/toolkit";
import { STATUS } from "./OrganizationSlice";
import {
  deactivateOrganizationAsync,
  fetchDeactivationPreviewAsync,
  fetchOrganizationsV3Async,
  fetchOrganizationsV3StatsAsync,
} from "../thunks/OrganizationsV3Thunk";

// Row shape of GET /master-data/v3/organizations. v3 is camelCase throughout —
// do not reuse the v1 Organization type from OrganizationSlice.
export interface OrganizationV3 {
  id: number;
  name: string;
  displayName: string;
  parentOrgId: number | null;
  status: string; // "ACTIVE" | "DEACTIVATED"
  statusDate: string | null; // ISO 8601; can be null on legacy rows
  activeUsers: number; // users directly in this org only (not the subtree)
  activeSubOrgs: number; // direct active children only (not the subtree)
}

// GET /master-data/v3/organizations/stats. The field names do not change between
// screen states: totalActiveSubOrgs is system-wide without parentId and
// recursive-below-the-org with it — the card labels change, not the fields.
export interface OrganizationsV3Stats {
  totalActiveSubOrgs: number;
  totalActiveUsers: number;
}

// GET /master-data/v3/organizations/{orgId}/deactivation-preview. userCount is the
// full cascade blast radius (invited/signed-up users and staff included), which is
// intentionally larger than the "active users" shown in the table.
export interface DeactivationPreview {
  organizationId: number;
  name: string;
  status: string;
  subOrgCount: number;
  userCount: number;
}

// POST /master-data/v3/organizations/{orgId}/deactivate response.
export interface DeactivateResult {
  organizationId: number;
  cascadeId: string | null;
  deactivatedOrganizations: number;
  deactivatedUsers: number;
  alreadyDeactivated: boolean;
}

interface OrganizationsV3State {
  organizations: OrganizationV3[];
  page: number;
  totalPages: number;
  totalItems: number;
  listStatus: string;
  listError: string | null;
  stats: OrganizationsV3Stats | null;
  statsStatus: string;
  deactivationPreview: DeactivationPreview | null;
  previewStatus: string;
  previewError: string | null;
  deactivateStatus: string;
  deactivateError: string | null;
}

const initialState: OrganizationsV3State = {
  organizations: [],
  page: 1,
  totalPages: 0,
  totalItems: 0,
  listStatus: STATUS.IDLE,
  listError: null,
  stats: null,
  statsStatus: STATUS.IDLE,
  deactivationPreview: null,
  previewStatus: STATUS.IDLE,
  previewError: null,
  deactivateStatus: STATUS.IDLE,
  deactivateError: null,
};

const organizationsV3Slice = createSlice({
  name: "organizationsV3",
  initialState,
  reducers: {
    resetOrganizationsV3: () => initialState,
    // Called every time the deactivate dialog opens so stale preview counts or a
    // previous attempt's error never leak into a new confirmation.
    resetDeactivationState: (state) => {
      state.deactivationPreview = null;
      state.previewStatus = STATUS.IDLE;
      state.previewError = null;
      state.deactivateStatus = STATUS.IDLE;
      state.deactivateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationsV3Async.pending, (state) => {
        state.listStatus = STATUS.LOADING;
        state.listError = null;
      })
      .addCase(fetchOrganizationsV3Async.fulfilled, (state, action) => {
        state.listStatus = STATUS.SUCCESS;
        state.organizations = action.payload?.items ?? [];
        state.page = action.payload?.page ?? 1;
        state.totalPages = action.payload?.totalPages ?? 0;
        state.totalItems = action.payload?.totalItems ?? 0;
      })
      .addCase(fetchOrganizationsV3Async.rejected, (state, action) => {
        state.listStatus = STATUS.FAILED;
        state.listError = (action.payload as string) ?? "An unexpected error occurred";
        state.organizations = [];
      })

      .addCase(fetchOrganizationsV3StatsAsync.pending, (state) => {
        state.statsStatus = STATUS.LOADING;
      })
      .addCase(fetchOrganizationsV3StatsAsync.fulfilled, (state, action) => {
        state.statsStatus = STATUS.SUCCESS;
        state.stats = action.payload ?? null;
      })
      .addCase(fetchOrganizationsV3StatsAsync.rejected, (state) => {
        state.statsStatus = STATUS.FAILED;
        state.stats = null;
      })

      .addCase(fetchDeactivationPreviewAsync.pending, (state) => {
        state.previewStatus = STATUS.LOADING;
        state.previewError = null;
      })
      .addCase(fetchDeactivationPreviewAsync.fulfilled, (state, action) => {
        state.previewStatus = STATUS.SUCCESS;
        state.deactivationPreview = action.payload ?? null;
      })
      .addCase(fetchDeactivationPreviewAsync.rejected, (state, action) => {
        state.previewStatus = STATUS.FAILED;
        state.previewError = (action.payload as string) ?? "An unexpected error occurred";
        state.deactivationPreview = null;
      })

      .addCase(deactivateOrganizationAsync.pending, (state) => {
        state.deactivateStatus = STATUS.LOADING;
        state.deactivateError = null;
      })
      .addCase(deactivateOrganizationAsync.fulfilled, (state) => {
        state.deactivateStatus = STATUS.SUCCESS;
      })
      .addCase(deactivateOrganizationAsync.rejected, (state, action) => {
        state.deactivateStatus = STATUS.FAILED;
        state.deactivateError = (action.payload as string) ?? "An unexpected error occurred";
      });
  },
});

export const { resetOrganizationsV3, resetDeactivationState } = organizationsV3Slice.actions;
export default organizationsV3Slice.reducer;
